import React from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Box,
  Divider,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import {
  Person,
  Subscriptions,
  Payment,
  SupportAgent,
  History,
  Work,
  Group,
  AdminPanelSettings,
  Dashboard,
  ConfirmationNumber,
  Assignment,
  People,
  Drafts,
} from "@mui/icons-material";
import { BarChart3 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const drawerWidth = 240;

const menuConfig = {
  super_admin: [
    {
      section: "Overview",
      items: [
        { text: "Dashboard", icon: <Dashboard sx={{ fontSize: 20 }} />, path: "/super-admin/dashboard" },
      ],
    },
    {
      section: "Management",
      items: [
        { text: "Users", icon: <People sx={{ fontSize: 20 }} />, path: "/super-admin/user-history" },
        { text: "Recruiters Requests", icon: <AdminPanelSettings sx={{ fontSize: 20 }} />, path: "/super-admin/recruiters-requests" },
        { text: "All Recruiter History", icon: <History sx={{ fontSize: 20 }} />, path: "/super-admin/admin-history" },
      ],
    },
    {
      section: "Operations",
      items: [
        { text: "Jobs", icon: <Work sx={{ fontSize: 20 }} />, path: "/super-admin/jobs-history" },
        { text: "Interviews", icon: <History sx={{ fontSize: 20 }} />, path: "/super-admin/interview-history" },
        { text: "All Support Tickets", icon: <ConfirmationNumber sx={{ fontSize: 20 }} />, path: "/allsupporttickets" },
      ],
    },
    {
      section: "Financial",
      items: [
        { text: "All Payments History", icon: <Payment sx={{ fontSize: 20 }} />, path: "/super-admin/payments-history" },
        { text: "All Subscription History", icon: <Subscriptions sx={{ fontSize: 20 }} />, path: "/super-admin/allsubscriptions" },
      ],
    },
    {
      section: "Account",
      items: [
        { text: "Profile", icon: <Person sx={{ fontSize: 20 }} />, path: "/profile" },
      ],
    },
  ],

 admin: [
   {
     section: "Overview",
     items: [
       { text: "Dashboard", icon: <Dashboard sx={{ fontSize: 20 }} />, path: "/admin/dashboard" },
     ],
   },
   {
     section: "Recruitment",
     items: [
       { text: "Jobs Posted", icon: <Work sx={{ fontSize: 20 }} />, path: "/admin/jobs" },
       { text: "Applications", icon: <Assignment sx={{ fontSize: 20 }} />, path: "/recruiter-applications" },
       { text: "Interviews", icon: <History sx={{ fontSize: 20 }} />, path: "/job-interview-history" },
       { text: "Offer Letter Templates", icon: <Drafts sx={{ fontSize: 20 }} />, path: "/admin/offer-letters" },
     ],
   },
   {
     section: "Financial",
     items: [
       { text: "Payments", icon: <Payment sx={{ fontSize: 20 }} />, path: "/payment" },
       { text: "Subscription", icon: <Subscriptions sx={{ fontSize: 20 }} />, path: "/subscription" },
     ],
   },
   {
     section: "Support",
     items: [
       { text: "Support", icon: <SupportAgent sx={{ fontSize: 20 }} />, path: "/support" },
     ],
   },
   {
     section: "Account",
     items: [
       { text: "Profile", icon: <Person sx={{ fontSize: 20 }} />, path: "/profile" },
     ],
   },
 ],

  candidate: [
    {
      section: "Overview",
      items: [
        { text: "Dashboard", icon: <Dashboard />, path: "/candidate/dashboard" },
      ],
    },
    {
      section: "Career",
      items: [
        { text: "My Applications", icon: <Assignment />, path: "/my-applications" },
        { text: "Interview History", icon: <History />, path: "/my-interview-history" },
      ],
    },
    {
      section: "Support",
      items: [
        { text: "Support", icon: <SupportAgent />, path: "/support" },
      ],
    },
    {
      section: "Account",
      items: [
        { text: "Profile", icon: <Person />, path: "/profile" },
      ],
    },
  ],
};

function Sidebar({ mobileOpen, handleDrawerToggle }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  if (!isAuthenticated) return null;

  const role = String(user?.role || '').trim().toLowerCase();
  const hasAnalyticsAccess =
    user?.plan === 'professional' || user?.plan === 'annual' || user?.plan === 'monthly';

  // Build admin sections dynamically to inject Analytics after Interviews
  const buildAdminSections = () => {
    const base = menuConfig['admin'] || [];
    if (!hasAnalyticsAccess) return base;

    return base.map(section => {
      if (section.section === 'Recruitment') {
        const items = [...section.items];
        const interviewIdx = items.findIndex(i => i.text === 'Interviews');
        const analyticsItem = {
          text: 'Analytics',
          icon: <BarChart3 size={20} />,
          path: '/analytics',
          badge: user?.plan === 'professional' || user?.plan === 'annual' ? 'PRO' : null,
        };
        if (interviewIdx >= 0) {
          items.splice(interviewIdx + 1, 0, analyticsItem);
        } else {
          items.push(analyticsItem);
        }
        return { ...section, items };
      }
      return section;
    });
  };

  const sections = role === 'admin' ? buildAdminSections() : (menuConfig[role] || []);

  const drawerContent = (
      <Box sx={{ height: "100%", overflowY: "auto", px: 1 }}>
        {sections.map((section, index) => (
            <Box key={section.section} sx={{ mt: 2 }}>
              <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    color: "#a0aec0",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: '0.65rem',
                    letterSpacing: '0.05em'
                  }}
              >
                {section.section}
              </Typography>

              <List sx={{ mt: 0.5 }}>
                {section.items.map((item) => (
                    <ListItemButton
                        key={item.text}
                        onClick={() => {
                          navigate(item.path);
                          if (handleDrawerToggle) handleDrawerToggle();
                        }}
                        selected={location.pathname === item.path}
                        sx={{
                          borderRadius: '8px',
                          mx: 1,
                          mb: 0.5,
                          py: 1,
                          "&.Mui-selected": {
                            backgroundColor: "#F8FAFC",
                            color: 'primary.main',
                            "& .MuiListItemIcon-root": {
                              color: 'primary.main'
                            },
                            "& .MuiTypography-root": {
                                fontWeight: 600
                            },
                            "&:after": {
                              content: '""',
                              position: 'absolute',
                              left: -8,
                              top: '20%',
                              bottom: '20%',
                              width: '4px',
                              backgroundColor: 'primary.main',
                              borderRadius: '0 4px 4px 0'
                            }
                          },
                          "&:hover": {
                            backgroundColor: "#f7fafc"
                          }
                        }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                            fontSize: '0.875rem',
                            color: location.pathname === item.path ? theme.palette.primary.main : '#4a5568',
                            fontWeight: location.pathname === item.path ? 600 : 500
                        }}
                      />
                      {item.badge && (
                        <Chip
                          label={item.badge}
                          size="small"
                          sx={{
                            bgcolor: '#7c3aed',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 18,
                            borderRadius: '999px',
                            px: 0.5,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      )}
                    </ListItemButton>
                ))}
              </List>

              {index !== sections.length - 1 && <Divider sx={{ my: 1 }} />}
            </Box>
        ))}
      </Box>
  );

  return (
      <>
        {/* Mobile Drawer */}
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                top: 0,
                height: "100%",
              },
            }}
        >
          <Box sx={{ py: 2, px: 2, display: "flex", alignItems: "center" }}>
            <img src="/logo.png" alt="ExiHire Logo" style={{ height: 50 }} />
          </Box>
          <Divider />
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                boxSizing: "border-box",
                // ✅ FIX for Issue 11:
                // Setting position to sticky and height based on viewport
                // minus Navbar ensures it doesn't push past the Footer.
                position: "sticky",
                top: "64px",
                height: "calc(100vh - 64px)",
                backgroundColor: "#fcfcfc",
                borderRight: "1px solid #f0f0f0",
                zIndex: (theme) => theme.zIndex.appBar - 1, // Lower than Footer zIndex
              },
            }}
        >
          {drawerContent}
        </Drawer>
      </>
  );
}

export default Sidebar;
